/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'../../../app/nls/StringResources',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../lib/@waca/dashboard-common/dist/api/PropertiesProviderAPI'
], (StringResources, APIFactory, PropertiesProviderAPI) => {
	const TAB_POSITION = [
		{
			'value': 'top',
			'label': StringResources.get('propTop')
		},
		{
			'value': 'left',
			'label': StringResources.get('propLeft')
		},
		{
			'value': 'bottom',
			'label': StringResources.get('propBottom')
		},
		{
			'value': 'right',
			'label': StringResources.get('propRight')
		}
	];

	const TAB_ICON_POSITION = [
		{
			'value': 'right',
			'label': StringResources.get('propRightTitle')
		},
		{
			'value': 'left',
			'label': StringResources.get('propLeftTitle')
		},
		{
			'value': 'top',
			'label': StringResources.get('propTopTitle')
		},
		{
			'value': 'bottom',
			'label': StringResources.get('propBottomTitle')
		}
	];

	class TabProperties {
		constructor({ dashboardApi, dashboardAPI, features, content, options }) {
			this._dashboard = dashboardApi || dashboardAPI;
			this._content = content;
			this._icons = features['Dashboard.Icons'];
			this._allowHidingMultipleTabs = !options || options.allowHidingMultipleTabs !== false;
		}

		initialize() {
			this._content.getFeature('Properties').registerProvider(this.getAPI());
		}

		_getCanvas() {
			if (!this._canvas) {
				this._canvas = this._dashboard.getCanvas();
			}
			return this._canvas;
		}

		getLayoutModel() {
			const boardModel = this._dashboard.getFeature('internal').getBoardModel();
			return boardModel.layout.findModel(this._content.getId());
		}

		getAPI() {
			return APIFactory.createAPI(this, [PropertiesProviderAPI]);
		}

		getPropertyList() {
			const propertyList = this._getTabPropertiesList();

			// Default the setPropertyValue and getPropertyValue for any property that doesn't define those methods
			propertyList.forEach((property) => {
				if (!property.setPropertyValue) {
					property.setPropertyValue = (value) => this._setPropertyValueInModel(property.id, value);
				}

				if (!property.getPropertyValue) {
					property.getPropertyValue = () => this._getPropertyValueFromModel(property.id);
				}
			});

			return propertyList;
		}

		getPropertyLayoutList() {
			const propertiesLayoutSpec = [{
				id: 'general',
				type: 'Group',
				label: StringResources.get('tabName_general')
			}, {
				id: 'tabs',
				type: 'Section',
				label: StringResources.get('sectionName_tabs'),
				position: 3
			}, {
				id: 'masterTabStyles',
				type: 'Section',
				collapsible: false,
				label: StringResources.get('propMasterTabStylesLabel')
			}, {
				id: 'individualTabStyles',
				type: 'Section',
				collapsible: false,
				label: StringResources.get('propIndividualTabStylesLabel')
			}];

			return propertiesLayoutSpec;
		}

		_individualTabSlideOut(page) {
			const propertiesManager = this._dashboard.getFeature('propertiesManager');
			return propertiesManager.getPropertyLayoutList([page]).then(properties => {
				return propertiesManager.addChild({
					overlay: true,
					label: StringResources.get('individualTabProperties'),
					content: {
						module: 'dashboard/contentpane/PropertyUIControlView',
						items: properties,
						content: page,
						refreshChild: true
					},
					pageChildId: page.getId()
				});
			});
		}

		_getIndividualTabStylesLinks() {
			const pages = this._getCanvas().findContent({ type: 'page' });
			return pages.map((page, index) => {
				const clickCallback = () => {
					this._individualTabSlideOut(page);
				};
				const tabIcon = page.getPropertyValue('tabIcon');
				return {
					id: 'tabIndividualStylingLink' + index,
					editor: {
						sectionId: 'general.tabs.individualTabStyles',
						uiControl: {
							type: 'SingleLineLinks',
							clickCallback: clickCallback,
							items: [{
								align: 'left',
								items: [{
									type: 'icon',
									svgIcon: tabIcon !== 'transparent' ? tabIcon : null,
									iconTooltip: StringResources.get('propIndividualTabStylesAriaLabel')
								}]
							}, {
								align: 'right',
								items: [{
									type: 'text',
									value: page.getPropertyValue('title'),
									showRightArrow: true
								}]
							}]
						}
					}
				};
			});
		}

		_getTabPropertiesList() {
			const restoreMasterStyling = () => {
				const transaction = this._dashboard.getFeature('Transaction');
				const transactionToken = transaction.startTransaction();
				this._getCanvas().setPropertyValue('tabTextColor', 'transparent', transactionToken);
				this._getCanvas().setPropertyValue('tabSelectedLineColor', 'transparent', transactionToken);
				this._getCanvas().setPropertyValue('tabBackgroundColor', 'transparent', transactionToken);
				transaction.endTransaction(transactionToken);
				this._dashboard.triggerDashboardEvent('properties:refreshPane', {
					focusSelector: '.clickCallback_left_0_tabRestoreDefaults'
				});
			};

			const model = this.getLayoutModel();
			const disableShowTabs = !this._allowHidingMultipleTabs && model.items.length > 1;
			return [{
				id: 'showTabsInPreview',
				supportsUndoRedo: true,
				getPropertyValue: () => !model.get('hideTab'),
				setPropertyValue: (value) => {
					const oldValue = !model.get('hideTab');
					if (!oldValue || oldValue !== value) {
						model.set({
							'hideTab': !value
						}, {
							payloadData: { skipUndoRedo: true }
						});
					}
				},
				editor: {
					sectionId: 'general.tabs',
					uiControl: {
						type: 'ToggleButton',
						label: StringResources.get('propShowTabsInPreview'),
						readOnly: disableShowTabs,
						disabled: disableShowTabs
					}
				}
			}, {
				id: 'tabPosition',
				supportsUndoRedo: true,
				getPropertyValue: () => model.get('tabPosition'),
				setPropertyValue: (value) => {
					const oldValue = model.get('tabPosition');
					if (!oldValue || oldValue !== value) {
						model.set({
							'tabPosition': value
						}, {
							payloadData: { skipUndoRedo: true }
						});
					}
				},
				editor: {
					sectionId: 'general.tabs',
					uiControl: {
						type: 'DropDown',
						label: StringResources.get('propTabPosition'),
						options: TAB_POSITION
					}
				}
			}, {
				id: 'tabIconPosition',
				supportsUndoRedo: true,
				getPropertyValue: () => model.get('tabIconPosition'),
				setPropertyValue: (value) => {
					const oldValue = model.get('tabIconPosition');
					if (!oldValue || oldValue !== value) {
						model.set({
							'tabIconPosition': value
						}, {
							payloadData: { skipUndoRedo: true }
						});
					}
				},
				editor: {
					sectionId: 'general.tabs',
					uiControl: {
						type: 'DropDown',
						label: StringResources.get('propTabIconPosition'),
						options: TAB_ICON_POSITION
					}
				}
			}, {
				id: 'tabTextColor',
				supportsUndoRedo: true,
				setPropertyValue: (value, transactionToken) => {
					this._changeTabColor('tabTextColor', value, transactionToken);
				},
				editor: {
					sectionId: 'general.tabs.masterTabStyles',
					uiControl: {
						type: 'ColorPicker',
						label: StringResources.get('propTabTextColor'),
						ariaLabel: StringResources.get('propTabTextColor'),
						paletteType: 'DashboardColorSet',
						open: false,
						showHexValue: true,
						addButton: true,
						onChange: (propertyName, propertyValueInfo = {}) => {
							this._getCanvas().setPropertyValue(propertyName, propertyValueInfo.name);
						}
					}
				}
			}, {
				id: 'tabSelectedLineColor',
				supportsUndoRedo: true,
				setPropertyValue: (value, transactionToken) => {
					this._changeTabColor('tabSelectedLineColor', value, transactionToken);
				},
				editor: {
					sectionId: 'general.tabs.masterTabStyles',
					uiControl: {
						type: 'ColorPicker',
						label: StringResources.get('propTabSelectedBarColor'),
						ariaLabel: StringResources.get('propTabSelectedBarColor'),
						paletteType: 'DashboardColorSet',
						open: false,
						showHexValue: true,
						addButton: true,
						onChange: (propertyName, propertyValueInfo = {}) => {
							this._getCanvas().setPropertyValue(propertyName, propertyValueInfo.name);
						}
					}
				}
			}, {
				id: 'tabBackgroundColor',
				supportsUndoRedo: true,
				setPropertyValue: (value, transactionToken) => {
					this._changeTabColor('tabBackgroundColor', value, transactionToken);
				},
				editor: {
					sectionId: 'general.tabs.masterTabStyles',
					uiControl: {
						type: 'ColorPicker',
						label: StringResources.get('propTabBackgroundColor'),
						ariaLabel: StringResources.get('propTabBackgroundColor'),
						paletteType: 'DashboardColorSet',
						open: false,
						showHexValue: true,
						addButton: true,
						onChange: (propertyName, propertyValueInfo = {}) => {
							this._getCanvas().setPropertyValue(propertyName, propertyValueInfo.name);
						}
					}
				}
			}, {
				id: 'tabRestoreDefaults',
				editor: {
					sectionId: 'general.tabs.masterTabStyles',
					uiControl: {
						type: 'SingleLineLinks',
						items: [{
							align: 'left',
							items: [{
								type: 'icon',
								class: 'iconTextLink',
								svgIcon: this._icons.getIcon('restore').id,
								iconTooltip: StringResources.get('propTabRestoreDefaults'),
								clickCallback: restoreMasterStyling
							}]
						}, {
							align: 'right',
							items: [{
								type: 'text',
								class: 'iconTextLink',
								value: StringResources.get('propTabRestoreDefaults'),
								ariaLabel: StringResources.get('propTabRestoreDefaults'),
								clickCallback: restoreMasterStyling
							}]
						}]
					}
				}
			}, {
				id: 'allowHidingMultipleTabs',
				getPropertyValue: () => this._allowHidingMultipleTabs,
				setPropertyValue: () => { throw new Error('not supported'); }
			}].concat(this._getIndividualTabStylesLinks());
		}

		_changeTabColor(propertyName, propertyValue) {
			// get layout and update CSS
			const payload = {};
			// Treat transparent as reset to default
			if(propertyValue) {
				payload[propertyName] = propertyValue == 'transparent' ? null : this._dashboard.getFeature('Colors').getColorClassName(propertyValue);
			} else {
				payload[propertyName] = null;
			}

			this._dashboard.getFeature('Colors').prepareForColorModelChange(payload, propertyName);

			const model = this.getLayoutModel();
			model.set(payload, {
				sender: this.senderId,
				payloadData: { skipUndoRedo: true }
			});
		}

		_getPropertyValueFromModel(propName) {
			const model = this.getLayoutModel();
			// In case of livewidget previews, we have a content but it is not attached to the layout, which causes the model to be null.
			return model && model.get(propName);
		}

		_setPropertyValueInModel(propName, propValue) {
			const payload = {};
			const model = this.getLayoutModel();
			payload[propName] = propValue;
			model.set(payload, {
				payloadData: { skipUndoRedo: true }
			});
		}
	}

	return TabProperties;
});
